import React, { useCallback, useEffect, useRef, useState } from "react";
const dateTime = (value) => value ? new Intl.DateTimeFormat("ru-RU", { dateStyle: "short", timeStyle: "short" }).format(new Date(value)) : "—";

export function BetaInvitationControls({ api, admissionOpen, busy, invitationCode, invitationRecipient, onInvite }) {
  const [open, setOpen] = useState(false);
  const [recipient, setRecipient] = useState("");
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState("");
  const [loading, setLoading] = useState(Boolean(api));
  const [error, setError] = useState("");
  const [inviteError, setInviteError] = useState("");
  const requestSeq = useRef(0);
  const load = useCallback(async (append = false) => {
    if (!api) return;
    const seq = ++requestSeq.current;
    setLoading(true); setError("");
    try {
      const page = await api.request(`/v1/platform/beta/invitations${append && nextCursor ? `?cursor=${encodeURIComponent(nextCursor)}` : ""}`);
      if (seq !== requestSeq.current) return;
      setItems((current) => append ? [...current, ...page.items.filter((item) => !current.some((old) => old.invitationId === item.invitationId))] : page.items);
      setNextCursor(page.nextCursor || "");
    } catch (failure) { if (seq === requestSeq.current) setError(failure.message || "Не удалось загрузить приглашения."); }
    finally { if (seq === requestSeq.current) setLoading(false); }
  }, [api, nextCursor]);
  useEffect(() => {
    setOpen(false); setRecipient(""); setInviteError(""); setItems([]); setNextCursor(""); load();
    return () => { ++requestSeq.current; };
  }, [api]);
  const submit = async (event) => {
    event.preventDefault();
    if (!recipient.trim() || busy) return;
    setInviteError("");
    try {
      const created = await onInvite(recipient.trim());
      if (created) {
        setOpen(false); setRecipient("");
        await load();
      }
    } catch (failure) { setInviteError(failure.message || "Не удалось создать приглашение."); }
  };
  return <section className="surface beta-invitations">
    <div className="section-heading"><div><h2>Приглашения</h2><p className="muted">Приглашение даёт доступ к разделу «Готовые решения». Набор доступных решений выбирается в настройках платформы.</p></div>
      {admissionOpen && <button type="button" className="button primary" disabled={busy} aria-expanded={open} aria-controls="beta-invitation-form" onClick={() => { setInviteError(""); setOpen(true); }}>Пригласить</button>}
    </div>
    {error && <p className="inline-problem" role="alert">{error}<button className="button ghost small" onClick={() => load()}>Повторить</button></p>}
    <div className="table-scroll"><table className="invitation-list"><thead><tr><th scope="col">Для кого</th><th scope="col">Создано</th><th scope="col">Действует до</th><th scope="col">Статус</th></tr></thead><tbody>
      {items.map((item) => <tr key={item.invitationId}><td>{item.recipient || "Не указан"}</td><td>{dateTime(item.createdAt)}</td><td>{dateTime(item.expiresAt)}</td><td>{({ pending: "Ожидает активации", activated: "Активировано", expired: "Срок истёк" })[item.status] || "—"}</td></tr>)}
    </tbody></table>{!items.length && <p className="muted">{loading ? "Загружаем приглашения…" : "Приглашений пока нет."}</p>}</div>
    {nextCursor && <button type="button" className="button ghost" disabled={loading} onClick={() => load(true)}>Загрузить ещё</button>}
    {open && admissionOpen && <form id="beta-invitation-form" className="invite-form" onSubmit={submit}>
      <h3>Новое приглашение</h3><label>Для кого<input autoFocus required maxLength={128} value={recipient} placeholder="Например, Иван" onChange={(event) => setRecipient(event.target.value)} disabled={busy}/></label>
      {inviteError && <p className="inline-problem" role="alert">{inviteError}</p>}
      <div className="corplinks-actions"><button className="button primary" disabled={busy || !recipient.trim()}>{busy ? "Создаём…" : "Создать приглашение"}</button><button type="button" className="button ghost" disabled={busy} onClick={() => { setOpen(false); setRecipient(""); }}>Отмена</button></div>
    </form>}
    {invitationCode && <div className="activation-result"><strong>{invitationRecipient ? `Код для ${invitationRecipient}` : "Код приглашения"}</strong><p>Одноразовый код показан только сейчас</p><code>{invitationCode}</code><button type="button" className="button ghost" onClick={() => navigator.clipboard?.writeText(invitationCode)}>Копировать</button></div>}
  </section>;
}

export function BetaSettings({ api }) {
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const [metrics, setMetrics] = useState(null);
  const [admissionOpen, setAdmissionOpen] = useState(false);
  const [invitationCode, setInvitationCode] = useState("");
  const [invitationRecipient, setInvitationRecipient] = useState("");
  const apiGeneration = useRef(0);
  useEffect(() => {
    ++apiGeneration.current;
    setInvitationCode(""); setInvitationRecipient(""); setBusy(false);
    setDraft(null); setMetrics(null); setError(""); setSaved(false); setAdmissionOpen(false);
    return () => { ++apiGeneration.current; };
  }, [api]);
  useEffect(() => {
    let cancelled = false;
    api.request("/v1/platform/beta/settings").then(({ settings }) => { if (!cancelled) setDraft(settings); })
      .catch((failure) => { if (!cancelled) setError(failure.message || "Не удалось загрузить настройки"); });
    api.request("/v1/platform/beta/metrics").then((result) => { if (!cancelled) { setMetrics(result.metrics); setAdmissionOpen(result.betaAdmissionOpen); } })
      .catch(() => { if (!cancelled) setError("Не удалось загрузить метрики беты."); });
    return () => { cancelled = true; };
  }, [api]);
  const update = (name) => (event) => { setSaved(false); setDraft((value) => ({ ...value, [name]: event.target.value })); };
  const save = async (event) => {
    event.preventDefault(); setBusy(true); setError(""); setSaved(false);
    try {
      const result = await api.request("/v1/platform/beta/settings", { method: "PUT", body: {
        revision: draft.revision, trialBudget: draft.trialBudget,
        trialDurationDays: Number(draft.trialDurationDays), pilotBudget: draft.pilotBudget,
        pilotOrganizationLimit: Number(draft.pilotOrganizationLimit),
      } });
      setDraft(result.settings); setSaved(true);
    } catch (failure) { setError(failure.message || "Не удалось сохранить настройки"); }
    finally { setBusy(false); }
  };
  const invite = async (recipient) => {
    const generation = apiGeneration.current;
    setBusy(true); setError(""); setInvitationCode("");
    try { const result = await api.request("/v1/platform/beta/invitations", { method: "POST", body: { recipient } });
      if (generation !== apiGeneration.current) return null;
      setInvitationCode(`${result.activation.organizationId}:${result.activation.userId}.${result.activation.token}`);
      setInvitationRecipient(result.invitation.recipient);
      return result.invitation;
    } catch (failure) { if (generation === apiGeneration.current) throw failure; return null; }
    finally { if (generation === apiGeneration.current) setBusy(false); }
  };
  return <><section className="invite-form beta-settings">
    <h3>Бесплатная бета</h3>
    <p className="muted">Бюджеты задаются в рублях.</p>
    <p className="muted">{admissionOpen ? "Допуск по приглашениям включён. Стартовый баланс назначается перед одобрением заявки в разделе «Организации»." : "Допуск внешних организаций откроется после технической приёмки. Сохранение параметров пока не включает бету."}</p>
    {error && <p className="inline-problem" role="alert">{error}</p>}
    {!draft && !error && <p className="muted">Загружаем настройки…</p>}
    {draft && <form onSubmit={save}><div className="form-grid">
      <label>Стартовый баланс по умолчанию, ₽<input type="number" min="0" step="0.01" value={draft.trialBudget} onChange={update("trialBudget")} required /></label>
      <label>Срок пробного бюджета, дней<input type="number" min="0" max="365" step="1" value={draft.trialDurationDays} onChange={update("trialDurationDays")} required /></label>
      <label>Общий бюджет пилота, ₽<input type="number" min="0" step="0.01" value={draft.pilotBudget} onChange={update("pilotBudget")} required /></label>
      <label>Предел числа организаций<input type="number" min="0" max="10000" step="1" value={draft.pilotOrganizationLimit} onChange={update("pilotOrganizationLimit")} required /></label>
    </div><div className="corplinks-actions"><button className="button primary" disabled={busy}>{busy ? "Сохраняем…" : "Сохранить параметры беты"}</button>{saved && <span role="status">Настройки сохранены</span>}</div></form>}
    {metrics && <div className="form-grid">
      <div>Активировано организаций<strong>{metrics.activatedOrganizations}</strong></div>
      <div>Получили результат<strong>{metrics.organizationsWithResult}</strong></div>
      <div>Повторно использовали<strong>{metrics.reusedOrganizations}</strong></div>
      <div>Запуски / результаты / ошибки<strong>{metrics.tasks} / {metrics.successfulResults} / {metrics.failedTasks}</strong></div>
    </div>}
  </section>
    <BetaInvitationControls api={api} admissionOpen={admissionOpen} busy={busy} invitationCode={invitationCode} invitationRecipient={invitationRecipient} onInvite={invite}/>
  </>;
}
